import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import type { SyncChaptersResult } from '../../services/types';

interface SyncChaptersModalProps {
  novelId: string;
  novelTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Stage = 'checking' | 'preview' | 'syncing' | 'done';

export function SyncChaptersModal({
  novelId,
  novelTitle,
  isOpen,
  onClose,
  onSuccess,
}: SyncChaptersModalProps) {
  const [stage, setStage] = useState<Stage>('checking');
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncChaptersResult | null>(null);

  // 檢查新章節
  const checkForUpdates = useCallback(async () => {
    setStage('checking');
    setError(null);

    try {
      const result = await api.syncChapters(novelId, true); // dryRun=true
      setSyncResult(result);
      setStage('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : '檢查更新失敗');
      setStage('preview');
    }
  }, [novelId]);

  // 執行同步
  const handleSync = useCallback(async () => {
    setStage('syncing');
    setError(null);

    try {
      const result = await api.syncChapters(novelId, false); // dryRun=false
      setSyncResult(result);
      setStage('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : '同步失敗');
      setStage('preview');
    }
  }, [novelId]);

  // 完成後關閉
  const handleDone = useCallback(() => {
    onSuccess();
    onClose();
  }, [onSuccess, onClose]);

  // 初始化檢查
  useEffect(() => {
    if (isOpen) {
      // Reset state when opening
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStage('checking');
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSyncResult(null);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(null);
      // Start checking for updates
      checkForUpdates();
    }
  }, [isOpen, checkForUpdates]);

  if (!isOpen) return null;

  return (
    <div className="modal">
      {/* Backdrop */}
      <div className="overlay" onClick={stage !== 'syncing' ? onClose : undefined} />

      {/* Modal */}
      <div className="modal-content max-w-2xl">
        {/* Header */}
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-button bg-primary-500 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <h3 className="heading-section">檢查更新</h3>
              <p className="body-small truncate max-w-md">{novelTitle}</p>
            </div>
          </div>

          {stage !== 'syncing' && (
            <button onClick={onClose} className="btn btn-ghost btn-icon">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-5 mb-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-button flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Content */}
        <div className="card-body">
          {/* Stage: Checking */}
          {stage === 'checking' && (
            <div className="py-8 text-center">
              <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
              <p className="body-base">正在檢查來源網站更新...</p>
              <p className="body-small mt-2">這可能需要幾秒鐘</p>
            </div>
          )}

          {/* Stage: Preview */}
          {stage === 'preview' && syncResult && (
            <div className="space-y-4">
              {syncResult.new_count === 0 ? (
                // 沒有新章節
                <div className="py-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success-50 dark:bg-success-900/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="heading-card">已是最新</p>
                  <p className="body-small mt-2">
                    目前共有 {syncResult.existing_count} 章，來源網站沒有新章節
                  </p>
                </div>
              ) : (
                // 有新章節
                <>
                  <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/50 rounded-card">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">{syncResult.new_count}</span>
                      </div>
                      <div>
                        <p className="font-medium text-primary-700 dark:text-primary-300">
                          發現 {syncResult.new_count} 個新章節
                        </p>
                        <p className="text-sm text-primary-600 dark:text-primary-400">
                          現有 {syncResult.existing_count} 章 → 同步後 {syncResult.existing_count + syncResult.new_count} 章
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 新章節列表 */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-card max-h-64 overflow-y-auto">
                    {syncResult.new_chapters.map((chapter, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-700/50 last:border-b-0"
                      >
                        <span className="flex-shrink-0 w-10 text-center badge badge-primary text-xs">
                          #{chapter.index}
                        </span>
                        <span className="truncate text-sm text-neutral-700 dark:text-neutral-300">
                          {chapter.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Stage: Syncing */}
          {stage === 'syncing' && (
            <div className="py-8 text-center">
              <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
              <p className="body-base">正在同步新章節...</p>
              <p className="body-small mt-2">請勿關閉此視窗</p>
            </div>
          )}

          {/* Stage: Done */}
          {stage === 'done' && syncResult && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success-50 dark:bg-success-900/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="heading-card">同步完成</p>
              <p className="body-small mt-2">
                已新增 {syncResult.synced_count} 個章節，正在背景下載內容
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-footer flex items-center justify-end gap-3">
          {stage === 'preview' && syncResult?.new_count === 0 && (
            <button onClick={onClose} className="btn btn-primary btn-md">
              關閉
            </button>
          )}

          {stage === 'preview' && syncResult && syncResult.new_count > 0 && (
            <>
              <button onClick={onClose} className="btn btn-ghost btn-md">
                取消
              </button>
              <button onClick={handleSync} className="btn btn-primary btn-md">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                開始同步
              </button>
            </>
          )}

          {stage === 'done' && (
            <button onClick={handleDone} className="btn btn-primary btn-md">
              完成
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
